import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Pencil, X, Plus, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import {
  addCaseFlag, removeCaseFlag, fetchFlagAutocomplete,
} from '@/lib/api';

// Server-side regex mirror — we validate client-side so the user sees
// a clear error before the round-trip. The backend re-validates with
// the same pattern. UPPER_SNAKE_CASE 3–64 chars.
const FLAG_PATTERN = /^[A-Z][A-Z0-9_]{2,63}$/;

/**
 * <CaseFlagEditor /> — pencil/link trigger + modal for live-editing a
 * case's `flags[]`. Hits the admin endpoints:
 *   POST   /admin/cases/{id}/flags
 *   DELETE /admin/cases/{id}/flags/{flag}
 *   GET    /admin/flags/autocomplete?q=
 *
 * Props:
 *   - caseId          : string (the `id` field on the case doc)
 *   - flags           : string[]  (current flags from parent state)
 *   - onFlagsChange   : (newFlags: string[]) => void
 *   - trigger         : 'pencil' | 'link'  (default 'pencil')
 *
 * The parent owns the source-of-truth flag list — every successful
 * add/remove fires `onFlagsChange(server.flags)` with the post-update
 * list straight from the server so the parent never drifts.
 */
export default function CaseFlagEditor({
  caseId, flags, onFlagsChange, trigger = 'pencil',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [busyFlag, setBusyFlag] = useState(null);   // flag currently being removed/added
  const debounceRef = useRef(null);

  // Debounced autocomplete fetch. Fires whenever the query changes
  // while the modal is open. Empty query → top-N alphabetical list.
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoadingSuggestions(true);
      try {
        const res = await fetchFlagAutocomplete(query.trim(), 12);
        setSuggestions(res?.flags || []);
      } catch (e) {
        setSuggestions([]);
      } finally {
        setLoadingSuggestions(false);
      }
    }, 180);
    return () => clearTimeout(debounceRef.current);
  }, [query, open]);

  const handleRemove = async (flag) => {
    setBusyFlag(flag);
    try {
      const res = await removeCaseFlag(caseId, flag);
      onFlagsChange?.(res?.flags || []);
      toast.success(`Removed ${flag}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message
        || 'Failed to remove flag';
      toast.error(msg);
    } finally {
      setBusyFlag(null);
    }
  };

  const handleAdd = async (rawFlag) => {
    const flag = (rawFlag || query || '').trim().toUpperCase();
    if (!flag) {
      toast.error('Type a flag name');
      return;
    }
    if (!FLAG_PATTERN.test(flag)) {
      toast.error('Flags must be UPPER_SNAKE_CASE (A-Z, 0-9, _, 3-64 chars)');
      return;
    }
    if ((flags || []).includes(flag)) {
      toast.message(`${flag} is already on this case`);
      return;
    }
    setBusyFlag(flag);
    try {
      const res = await addCaseFlag(caseId, flag);
      onFlagsChange?.(res?.flags || []);
      toast.success(
        res?.status === 'already_present'
          ? `${flag} was already present`
          : `Added ${flag}`,
      );
      setQuery('');
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message
        || 'Failed to add flag';
      toast.error(typeof msg === 'string' ? msg : 'Failed to add flag');
    } finally {
      setBusyFlag(null);
    }
  };

  const sortedFlags = [...(flags || [])].sort();
  const presentSet = new Set(sortedFlags);
  // Suggestions that aren't already on the case (so the click action
  // is meaningful — clicking a present flag would be a no-op).
  const actionableSuggestions = suggestions.filter((f) => !presentSet.has(f));

  return (
    <>
      {trigger === 'link' ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="case-flag-editor-trigger-link"
          className="text-xs font-medium text-blue-600 hover:text-blue-800
                     underline-offset-2 hover:underline inline-flex items-center gap-1">
          <Pencil className="w-3 h-3" />
          Edit flags
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Edit case flags"
          data-testid="case-flag-editor-trigger-pencil"
          title="Edit case flags"
          className="inline-flex items-center justify-center w-6 h-6 rounded
                     text-slate-500 hover:text-blue-600 hover:bg-blue-50
                     transition-colors">
          <Pencil className="w-3.5 h-3.5" />
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-lg"
          data-testid="case-flag-editor-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-4 h-4" />
              Edit case flags
            </DialogTitle>
            <DialogDescription>
              Add or remove flags directly. Changes save immediately and
              are recorded in the case audit log (changed_by: casey).
            </DialogDescription>
          </DialogHeader>

          {/* Current flags list */}
          <div className="space-y-2">
            <div className="text-xs font-medium uppercase tracking-wider text-slate-500">
              Current flags ({sortedFlags.length})
            </div>
            {sortedFlags.length === 0 ? (
              <div className="text-sm text-slate-400 italic py-2"
                   data-testid="case-flag-editor-empty">
                No flags set on this case yet.
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto pr-1"
                   data-testid="case-flag-editor-current-flags">
                {sortedFlags.map((f) => (
                  <Badge
                    key={f}
                    variant="secondary"
                    data-testid={`case-flag-editor-flag-${f}`}
                    className="bg-slate-100 text-slate-800 border border-slate-200
                               hover:bg-slate-100 pl-2 pr-1 py-0.5 gap-1
                               text-[11px] font-mono">
                    <span>{f}</span>
                    <button
                      type="button"
                      onClick={() => handleRemove(f)}
                      disabled={busyFlag === f}
                      aria-label={`Remove ${f}`}
                      data-testid={`case-flag-editor-remove-${f}`}
                      className="ml-0.5 inline-flex items-center justify-center
                                 w-4 h-4 rounded hover:bg-rose-100 hover:text-rose-700
                                 transition-colors disabled:opacity-50">
                      {busyFlag === f
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <X className="w-3 h-3" />}
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Add new flag */}
          <div className="space-y-2 mt-2">
            <div className="text-xs font-medium uppercase tracking-wider text-slate-500">
              Add a flag
            </div>
            <div className="flex items-center gap-2">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value.toUpperCase())}
                placeholder="e.g. DENTAL_COMPARISON_NEEDED"
                data-testid="case-flag-editor-input"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAdd();
                  }
                }}
                className="font-mono text-sm"
              />
              <Button
                type="button"
                onClick={() => handleAdd()}
                disabled={!query.trim() || busyFlag === query.trim().toUpperCase()}
                data-testid="case-flag-editor-add-btn"
                size="sm">
                {busyFlag === query.trim().toUpperCase()
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <><Plus className="w-4 h-4 mr-1" />Add</>}
              </Button>
            </div>
            {query && !FLAG_PATTERN.test(query.trim()) && (
              <div className="text-xs text-amber-700 flex items-center gap-1"
                   data-testid="case-flag-editor-validation-warn">
                <AlertTriangle className="w-3 h-3" />
                Must be UPPER_SNAKE_CASE (3–64 chars, A-Z 0-9 _)
              </div>
            )}

            {/* Autocomplete suggestions */}
            <div className="text-xs font-medium uppercase tracking-wider
                            text-slate-500 mt-2">
              {query ? 'Matching flags from corpus' : 'Suggestions'}
              {loadingSuggestions && (
                <Loader2 className="w-3 h-3 inline-block ml-1 animate-spin" />
              )}
            </div>
            <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto pr-1"
                 data-testid="case-flag-editor-suggestions">
              {actionableSuggestions.length === 0 && !loadingSuggestions && (
                <div className="text-xs text-slate-400 italic">
                  {query
                    ? 'No matching flags in corpus — use the input above to coin a new one.'
                    : 'No suggestions yet.'}
                </div>
              )}
              {actionableSuggestions.map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => handleAdd(f)}
                  disabled={busyFlag === f}
                  data-testid={`case-flag-editor-suggestion-${f}`}
                  className="text-[11px] font-mono px-2 py-0.5 rounded-full
                             border border-blue-200 bg-blue-50 text-blue-800
                             hover:bg-blue-100 hover:border-blue-300
                             disabled:opacity-50 transition-colors
                             inline-flex items-center gap-1">
                  {busyFlag === f
                    ? <Loader2 className="w-3 h-3 animate-spin" />
                    : <Plus className="w-3 h-3" />}
                  {f}
                </button>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              data-testid="case-flag-editor-close-btn">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
